/**
 * „Vor dem Versand" – die zwei Pflichtschritte eines noch nicht versendeten
 * Angebots: Angebotsprüfung und Ankündigungsanruf.
 *
 * Beides gehört dem Versender. Erst wenn beide Schritte erledigt oder mit
 * Begründung übersprungen sind, lässt sich „Gesendet" setzen – hier steht,
 * woran es gerade hängt und wie es weitergeht. Der Block ist damit die
 * Erklärung für das Schloss in der Statusauswahl.
 *
 * Die Knöpfe hier öffnen den Prüfblock, den Ankündigungsdialog oder die
 * Begründungsabfrage. Kein Schritt wird ohne Eingabe quittiert.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../api';
import { useKora, useMeta } from '../zustand';
import { dat } from '../hilfen/format';
import { UeberspringenModal, type Versandschritt } from '../modals/UeberspringenModal';
import type { Angebot, AngebotAntwort } from '../typen';
import './versandschritte.css';

type Ton = 'offen' | 'warten' | 'fertig' | 'ruhig';

export function Versandschritte({
  angebot,
  onPruefen,
  onAnkuendigen,
  onFertig,
}: {
  angebot: Angebot;
  /** Rollt zum Prüfblock – die Prüfung selbst läuft dort. */
  onPruefen: () => void;
  onAnkuendigen: () => void;
  onFertig: () => void;
}) {
  const meta = useMeta();
  const { toastZeigen } = useKora();
  const [ueberspringen, setUeberspringen] = useState<Versandschritt | null>(null);
  const v = angebot.versand;
  const p = angebot.pruefung;

  const nachholen = useMutation({
    mutationFn: (schritt: Versandschritt) =>
      api.sende<AngebotAntwort>(`/angebote/${angebot.id}/versand/nachholen`, { schritt }),
    onSuccess: (antwort) => {
      onFertig();
      if (antwort.toast) toastZeigen(antwort.toast);
    },
    onError: (f) => toastZeigen(f instanceof Error ? f.message : 'Das hat nicht geklappt.'),
  });

  /* ---- Prüfung ---- */
  const pruefungEingerichtet = meta.pruefungAktiv && p.moeglich;
  let pruefTon: Ton;
  let pruefText: string;
  if (!pruefungEingerichtet) {
    pruefTon = 'ruhig';
    pruefText = 'nicht eingerichtet – zählt nicht als fehlend';
  } else if (v.pruefungUebersprungen) {
    pruefTon = 'warten';
    pruefText = `übersprungen von ${v.pruefungUebersprungen.von} am ${v.pruefungUebersprungen.amText}`;
  } else if (!v.pruefungOffen) {
    pruefTon = 'fertig';
    pruefText = p.geprueftAmText ? `abgeschlossen · geprüft ${p.geprueftAmText} von ${p.geprueftVon}` : 'abgeschlossen';
  } else {
    pruefTon = 'offen';
    pruefText = p.nieGeprueft
      ? 'offen – noch nicht geprüft'
      : `offen – ${p.offen} ${p.offen === 1 ? 'Prüfpunkt braucht' : 'Prüfpunkte brauchen'} ein Urteil`;
  }

  /* ---- Ankündigung ---- */
  let ankTon: Ton;
  let ankText: string;
  if (v.ankuendigungUebersprungen) {
    ankTon = 'warten';
    ankText = `übersprungen von ${v.ankuendigungUebersprungen.von} am ${v.ankuendigungUebersprungen.amText}`;
  } else if (!v.ankuendigungOffen) {
    ankTon = 'fertig';
    ankText = v.erreichtAm ? `Kunde erreicht am ${dat(v.erreichtAm)}` : 'Kunde erreicht';
  } else if (v.versuche > 0) {
    ankTon = 'warten';
    ankText =
      `${v.versuche} ${v.versuche === 1 ? 'Versuch' : 'Versuche'}, nicht erreicht` +
      (v.erneutAm ? ` · erneut am ${dat(v.erneutAm)}` : '');
  } else {
    ankTon = 'offen';
    ankText = 'offen – der Kunde weiß noch nicht, dass gerechnet wurde';
  }

  const fertig = !v.pruefungOffen && !v.ankuendigungOffen;

  return (
    <div className={`vs ${fertig ? 'vs--fertig' : 'vs--offen'}`}>
      <div className="vs-kopf">
        <div>
          <div className="vs-kicker">Vor dem Versand</div>
          <div className="vs-schlagzeile">
            {fertig
              ? 'Versandfertig – „Status ändern → Gesendet" trägt den Versand ein.'
              : 'Zwei Schritte, bevor das Angebot hinausgeht.'}
          </div>
        </div>
      </div>

      <ol className="vs-liste">
        <li className={`vs-schritt vs-schritt--${pruefTon}`}>
          <span className="vs-punkt" />
          <div className="vs-text">
            <span className="vs-name">Angebotsprüfung</span>
            <span className="vs-stand">{pruefText}</span>
            {v.pruefungUebersprungen && (
              <span className="vs-begruendung">„{v.pruefungUebersprungen.begruendung}"</span>
            )}
          </div>
          <div className="vs-knoepfe">
            {pruefungEingerichtet && v.pruefungOffen && (
              <>
                <button type="button" className="vs-knopf vs-knopf--prim" onClick={onPruefen}>
                  Prüfen
                </button>
                <button type="button" className="vs-knopf" onClick={() => setUeberspringen('pruefung')}>
                  Überspringen
                </button>
              </>
            )}
            {v.pruefungUebersprungen && (
              <button
                type="button"
                className="vs-knopf"
                disabled={nachholen.isPending}
                onClick={() => nachholen.mutate('pruefung')}
              >
                Doch prüfen
              </button>
            )}
          </div>
        </li>

        <li className={`vs-schritt vs-schritt--${ankTon}`}>
          <span className="vs-punkt" />
          <div className="vs-text">
            <span className="vs-name">Ankündigungsanruf</span>
            <span className="vs-stand">{ankText}</span>
            {v.ankuendigungUebersprungen && (
              <span className="vs-begruendung">„{v.ankuendigungUebersprungen.begruendung}"</span>
            )}
          </div>
          <div className="vs-knoepfe">
            {v.ankuendigungOffen && (
              <>
                <button type="button" className="vs-knopf vs-knopf--prim" onClick={onAnkuendigen}>
                  {v.versuche > 0 ? 'Erneut anrufen' : 'Ankündigen'}
                </button>
                <button
                  type="button"
                  className="vs-knopf"
                  onClick={() => setUeberspringen('ankuendigung')}
                >
                  Überspringen
                </button>
              </>
            )}
            {v.ankuendigungUebersprungen && (
              <button
                type="button"
                className="vs-knopf"
                disabled={nachholen.isPending}
                onClick={() => nachholen.mutate('ankuendigung')}
              >
                Doch anrufen
              </button>
            )}
          </div>
        </li>
      </ol>

      {!fertig && (
        <p className="vs-fuss">
          Überspringen geht – mit Begründung. Sie bleibt am Vorgang und in der Chronik stehen.
        </p>
      )}

      {ueberspringen && (
        <UeberspringenModal
          angebot={angebot}
          schritt={ueberspringen}
          onSchliessen={() => setUeberspringen(null)}
          onFertig={onFertig}
        />
      )}
    </div>
  );
}
